/**
 * Hook V2 — useApprenantQualiopi
 *
 * Phase wiring 2 (27/05/2026) : branche sur la VRAIE route API
 * `GET /api/v1/v2/apprenants/:id/qualiopi`. Fallback mock automatique si l'API
 * échoue (401/404/réseau/501).
 *
 * Source de vérité : Postgres QualiopiV2Form (formulaires F1-F7 réels).
 */
import { useQuery } from '@tanstack/react-query';
import { api, ApiClientError } from '../../../lib/api.js';

export type FormStatus = 'completed' | 'pending' | 'sent' | 'never_sent' | 'error';

export interface QualiopiForm {
  code: 'F1' | 'F2' | 'F3' | 'F4' | 'F5' | 'F6' | 'F7';
  label: string;
  status: FormStatus;
  scheduledSendDate: string | null;
  sentAt: string | null;
  completedAt: string | null;
  zohoWrittenAt: string | null;
  score: number | null;
  overdueDays: number | null;
}

export interface ApprenantQualiopi {
  apprenantId: string;
  forms: QualiopiForm[];
  overallScore: number;
  satisfactionScore: number | null;
  derniereRelance: string | null;
  alerts: string[];
}

// ─── Fallback mock MARTINE (utilisé si API inaccessible) ───────────────
const MARTINE_MOCK: ApprenantQualiopi = {
  apprenantId: 'demo-martine',
  overallScore: 71,
  satisfactionScore: 4.3,
  derniereRelance: '2026-05-20',
  alerts: ['F4 mi-parcours envoyé le 15/05 — pas répondu depuis 12 jours'],
  forms: [
    {
      code: 'F1',
      label: 'Recueil besoins & positionnement',
      status: 'completed',
      scheduledSendDate: '2025-04-12',
      sentAt: '2025-04-12',
      completedAt: '2025-04-12',
      zohoWrittenAt: '2025-04-12T14:32:10Z',
      score: null,
      overdueDays: null,
    },
    {
      code: 'F2',
      label: 'Évaluation pré-formation',
      status: 'completed',
      scheduledSendDate: '2025-04-15',
      sentAt: '2025-04-15',
      completedAt: '2025-04-15',
      zohoWrittenAt: '2025-04-15T09:12:00Z',
      score: null,
      overdueDays: null,
    },
    {
      code: 'F3',
      label: 'Satisfaction à chaud (J+7)',
      status: 'completed',
      scheduledSendDate: '2025-04-22',
      sentAt: '2025-04-22',
      completedAt: '2025-04-22',
      zohoWrittenAt: '2025-04-22T18:05:00Z',
      score: 4.5,
      overdueDays: null,
    },
    {
      code: 'F4',
      label: 'Mi-parcours',
      status: 'pending',
      scheduledSendDate: '2025-05-12',
      sentAt: '2025-05-15',
      completedAt: null,
      zohoWrittenAt: null,
      score: null,
      overdueDays: null,
    },
    {
      code: 'F5',
      label: 'Satisfaction à chaud (fin)',
      status: 'never_sent',
      scheduledSendDate: '2026-05-20',
      sentAt: null,
      completedAt: null,
      zohoWrittenAt: null,
      score: null,
      overdueDays: 7,
    },
    {
      code: 'F6',
      label: 'Satisfaction à froid (J+90)',
      status: 'never_sent',
      scheduledSendDate: '2026-08-15',
      sentAt: null,
      completedAt: null,
      zohoWrittenAt: null,
      score: null,
      overdueDays: null,
    },
    {
      code: 'F7',
      label: "Évaluation formateur (l'élève par le formateur)",
      status: 'pending',
      scheduledSendDate: '2026-06-15',
      sentAt: null,
      completedAt: null,
      zohoWrittenAt: null,
      score: null,
      overdueDays: null,
    },
  ],
};

async function fetchQualiopi(id: string): Promise<ApprenantQualiopi & { source: 'api' | 'mock' }> {
  try {
    const data = await api.get<ApprenantQualiopi>(`/api/v1/v2/apprenants/${id}/qualiopi`);
    return { ...data, source: 'api' };
  } catch (err) {
    if (err instanceof ApiClientError && [401, 404, 501, 0].includes(err.status)) {
      return { ...MARTINE_MOCK, source: 'mock' };
    }
    return { ...MARTINE_MOCK, source: 'mock' };
  }
}

export function useApprenantQualiopi(id: string): {
  data: (ApprenantQualiopi & { source: 'api' | 'mock' }) | null;
  isLoading: boolean;
  error: Error | null;
} {
  const { data, isLoading, error } = useQuery({
    queryKey: ['v2', 'apprenant-qualiopi', id],
    queryFn: () => fetchQualiopi(id),
    staleTime: 60_000,
  });
  return { data: data ?? null, isLoading, error: (error as Error | null) ?? null };
}
